import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiFileText, FiAward, FiArrowLeft, FiBriefcase, FiBookOpen, FiFolder, FiHeart, FiUserCheck, FiUsers, FiMessageSquare } from "react-icons/fi";
import AppShell from "../components/layout/AppShell";
import { Avatar, Button, Card, Badge, Spinner } from "../components/ui";
import { getProfile } from "../api/bios";
import { getUserPosts } from "../api/posts";
import { getUser } from "../api/users";
import { getUserCommunities } from "../api/communities";
import uploadUrl from "../utils/assetUrl";
import { coerceArray } from "../utils/format";
import PostCard from "../components/common/PostCard";
import { AuthContext } from "../context/AuthContext";

export default function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [communityCount, setCommunityCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      getProfile(userId).catch(() => null),
      getUserPosts(userId).catch(() => ({ data: [] })),
      getUser(userId).catch(() => null),
      getUserCommunities(userId).catch(() => ({ data: [] })),
    ]).then(([profileRes, postsRes, userRes, communitiesRes]) => {
      if (!active) return;
      if (profileRes?.data?.success) setProfile(profileRes.data.profile);
      setPosts(postsRes.data?.data || []);
      setUser(userRes?.data?.user || null);
      setCommunityCount((communitiesRes.data?.data || []).length);
      setLoading(false);
    });
    return () => { active = false; };
  }, [userId]);

  if (loading) return <AppShell title="Profile" wide><div className="flex justify-center py-24"><Spinner className="h-8 w-8" /></div></AppShell>;

  const name = user ? `${user.name} ${user.lastName || ""}`.trim() : "Member";
  const avatarSrc = profile?.profileImage ? uploadUrl(profile.profileImage) : null;
  const skills = coerceArray(profile?.skills);
  const employment = coerceArray(profile?.employment);
  const education = coerceArray(profile?.education);
  const projects = coerceArray(profile?.projects);
  const volunteer = coerceArray(profile?.volunteer);
  const referees = coerceArray(profile?.referees);
  const isOwnProfile = currentUser && String(currentUser.id) === String(userId);

  return (
    <AppShell title={name} subtitle="Member profile" wide>
      {/* A real back navigation instead of a hardcoded "/communities" link
          that was wrong whenever someone arrived here from anywhere else
          (a post author, a chat message, search, an admin member list). */}
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
        <FiArrowLeft className="h-4 w-4" /> Back
      </button>

      <Card className="overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-primary-600 to-primary-800 sm:h-40" />
        <div className="flex flex-col gap-4 px-6 pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="-mt-12 flex items-end gap-4 sm:-mt-14">
            <Avatar src={avatarSrc} name={name} size="xl" ring />
            <div className="pb-1">
              <h2 className="text-xl font-bold text-slate-900">{name}</h2>
              {user?.roles === "admin" && <Badge variant="primary" className="mt-1">Admin</Badge>}
              <p className="text-sm text-slate-500">{user?.email}</p>
            </div>
          </div>
          {!isOwnProfile && (
            <Button size="sm" onClick={() => navigate(`/messages?with=${userId}`)}>
              <FiMessageSquare className="h-4 w-4" /> Message
            </Button>
          )}
        </div>
        {/* Same stat set as your own profile page (Communities, Posts,
            Skills) — previously showed Posts/Skills/Projects here instead,
            a content mismatch between the two views of the same data. */}
        <div className="grid grid-cols-3 gap-3 border-t border-slate-100 px-6 py-4 sm:max-w-sm">
          <Stat icon={<FiUsers />} value={communityCount} label="Communities" />
          <Stat icon={<FiFileText />} value={posts.length} label="Posts" />
          <Stat icon={<FiAward />} value={skills.length} label="Skills" />
        </div>
      </Card>

      {profile?.Bio && (
        <Card className="mt-6 p-6">
          <h3 className="text-sm font-semibold text-slate-900">About</h3>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{profile.Bio}</p>
        </Card>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {skills.length > 0 && (
          <Card className="p-5">
            <div className="mb-3 flex items-center gap-2"><FiAward className="h-4 w-4 text-primary-500" /><h3 className="text-sm font-semibold text-slate-900">Skills</h3></div>
            <div className="flex flex-wrap gap-2">{skills.map((s,i) => <Badge key={i} variant="primary">{s}</Badge>)}</div>
          </Card>
        )}
        {employment.length > 0 && (
          <Card className="p-5">
            <div className="mb-3 flex items-center gap-2"><FiBriefcase className="h-4 w-4 text-primary-500" /><h3 className="text-sm font-semibold text-slate-900">Experience</h3></div>
            <ul className="space-y-2">{employment.map((e,i) => <li key={i} className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{e}</li>)}</ul>
          </Card>
        )}
        {education.length > 0 && (
          <Card className="p-5">
            <div className="mb-3 flex items-center gap-2"><FiBookOpen className="h-4 w-4 text-primary-500" /><h3 className="text-sm font-semibold text-slate-900">Education</h3></div>
            <ul className="space-y-2">{education.map((e,i) => <li key={i} className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{e}</li>)}</ul>
          </Card>
        )}
        {projects.length > 0 && (
          <Card className="p-5">
            <div className="mb-3 flex items-center gap-2"><FiFolder className="h-4 w-4 text-primary-500" /><h3 className="text-sm font-semibold text-slate-900">Projects</h3></div>
            <ul className="space-y-2">{projects.map((p,i) => <li key={i} className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{p}</li>)}</ul>
          </Card>
        )}
        {/* Volunteer experience and Referees — previously missing here
            entirely, even though your own profile page has always shown
            them. Same underlying data, so both views should show it. */}
        {volunteer.length > 0 && (
          <Card className="p-5">
            <div className="mb-3 flex items-center gap-2"><FiHeart className="h-4 w-4 text-primary-500" /><h3 className="text-sm font-semibold text-slate-900">Volunteer experience</h3></div>
            <ul className="space-y-2">{volunteer.map((v,i) => <li key={i} className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{v}</li>)}</ul>
          </Card>
        )}
        {referees.length > 0 && (
          <Card className="p-5">
            <div className="mb-3 flex items-center gap-2"><FiUserCheck className="h-4 w-4 text-primary-500" /><h3 className="text-sm font-semibold text-slate-900">Referees</h3></div>
            <ul className="space-y-2">{referees.map((r,i) => <li key={i} className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{r}</li>)}</ul>
          </Card>
        )}
      </div>

      {posts.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">Posts by {name}</h3>
          <div className="space-y-4">{posts.map(post => <PostCard key={post.id} post={post} author={user} />)}</div>
        </div>
      )}
    </AppShell>
  );
}

function Stat({ icon, value, label }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2.5 text-center">
      <div className="flex items-center justify-center gap-1.5 text-primary-600">{icon}</div>
      <p className="mt-1 text-lg font-bold text-slate-900">{value}</p>
      <p className="text-[11px] text-slate-500">{label}</p>
    </div>
  );
}
